import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Filter, Mail, Sparkles, Send, Loader, X, StickyNote, Copy, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const STATUS_COLORS = {
    prospect: "border-slate-500/40 text-slate-600",
    invited: "border-sky-500/40 text-sky-600",
    send_failed: "border-rose-500/50 text-rose-600 bg-rose-500/10",
    opened_email: "border-indigo-500/40 text-indigo-600",
    clicked: "border-violet-500/40 text-violet-600",
    passport_started: "border-fuchsia-500/40 text-fuchsia-600",
    completed: "border-emerald-500/40 text-emerald-600",
    verification_pending: "border-amber-500/40 text-amber-600",
    verified: "border-emerald-600/60 text-emerald-700",
    interviewing: "border-sky-600/60 text-sky-700",
    placed: "border-emerald-700 text-emerald-800",
    archived: "border-slate-400 text-slate-500",
};

const STATUS_LABEL = (s) => s.replace(/_/g, " ");

// Ordered chip filters — Send Failed sits between All + Invited so it's
// impossible for ops to miss when Resend has an outage.
const CHIP_FILTERS = [
    { key: "", label: "All" },
    { key: "send_failed", label: "Send Failed", Icon: AlertTriangle, tone: "rose" },
    { key: "invited", label: "Invited" },
    { key: "opened_email", label: "Opened" },
    { key: "clicked", label: "Clicked" },
    { key: "passport_started", label: "Passport Started" },
    { key: "verified", label: "Verified" },
    { key: "placed", label: "Placed" },
    { key: "archived", label: "Archived" },
];

export default function RecruitCRM() {
    const [items, setItems] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [filter, setFilter] = useState("");
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [retryingId, setRetryingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/recruit/crm/candidates${filter ? `?status=${filter}` : ""}`);
            setItems(data.items || []);
            setStatuses(data.statuses || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);

    // Pull per-status counts once on mount + after every filter change so
    // the chip row shows accurate totals across ALL rows, not just the
    // currently-filtered slice.
    const loadCounts = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/recruit/crm/candidates?limit=500");
            const c = {};
            (data.items || []).forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
            c[""] = (data.items || []).length;
            setCounts(c);
        } catch { /* silent — chip counts are cosmetic */ }
    }, []);
    useEffect(() => { loadCounts(); }, [loadCounts, items.length]);

    const retrySend = async (invitationId, e) => {
        e?.stopPropagation?.();
        setRetryingId(invitationId);
        try {
            await api.post(`/admin/recruit/crm/candidates/${invitationId}/retry-send`);
            toast.success("Retry queued — check back in a few seconds.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setRetryingId(null); }
    };

    return (
        <div data-testid="recruit-crm" className="space-y-4">
            <div className="card-soft p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-heading font-medium text-lg">Talent CRM</h2>
                        <p className="text-sm text-muted-foreground">Every candidate you&apos;ve invited, across all searches and campaigns.</p>
                    </div>
                    <div className="inline-flex items-center gap-2">
                        <Filter size={13} className="text-muted-foreground" />
                        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="crm-status-filter" className="text-sm bg-background border border-border rounded-md px-2 py-1">
                            <option value="">All statuses ({items.length})</option>
                            {statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL(s)}</option>)}
                        </select>
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="crm-chip-filters">
                    {CHIP_FILTERS.map((c) => {
                        const n = counts[c.key] || 0;
                        const active = filter === c.key;
                        const isFailed = c.key === "send_failed";
                        return (
                            <button
                                key={c.key || "all"}
                                onClick={() => setFilter(c.key)}
                                data-testid={`crm-chip-${c.key || "all"}`}
                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1 ${
                                    active
                                        ? (isFailed
                                            ? "border-rose-500 bg-rose-500/15 text-rose-700 dark:text-rose-300 font-semibold"
                                            : "border-emerald-500 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-semibold")
                                        : (isFailed && n > 0
                                            ? "border-rose-500/40 bg-rose-500/5 text-rose-700 dark:text-rose-400"
                                            : "border-border bg-background hover:bg-muted text-muted-foreground")
                                }`}
                            >
                                {c.Icon && <c.Icon size={11} />} {c.label} <span className="tabular-nums opacity-70">{n}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
            {loading ? <div className="py-12 flex justify-center"><Loader2 size={20} className="animate-spin text-violet-500" /></div>
                : items.length === 0 ? (
                    <div className="card-soft p-10 text-center">
                        <Mail size={22} className="mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mt-2">No CRM records yet — invitations sent from Candidate Search will appear here.</p>
                    </div>
                ) : (
                    <div className="card-soft divide-y divide-border" data-testid="crm-list">
                        {items.map((i) => (
                            <button key={i.id} onClick={() => setDetail(i)} data-testid={`crm-row-${i.id}`} className="w-full py-3 px-4 flex items-center justify-between gap-3 hover:bg-muted/30 text-left">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium truncate">{i.candidate_name || i.candidate_email}</span>
                                        <Badge variant="outline" className={STATUS_COLORS[i.status] || ""}>{STATUS_LABEL(i.status)}</Badge>
                                        {i.candidate_source === "apollo" && <Badge variant="outline" className="text-[10px] border-sky-500/40 text-sky-600">Apollo</Badge>}
                                        {i.retry_count > 0 && (
                                            <Badge variant="outline" className="text-[10px] border-slate-400 text-slate-500">retry × {i.retry_count}</Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                                        {i.candidate_email} · invited {new Date(i.created_at).toLocaleDateString()}
                                        {i.opened_at && <> · opened {new Date(i.opened_at).toLocaleDateString()}</>}
                                        {i.clicked_at && <> · clicked {new Date(i.clicked_at).toLocaleDateString()}</>}
                                        {i.status === "send_failed" && i.send_error && (
                                            <span className="text-rose-600 dark:text-rose-400"> · <span title={i.send_error}>{i.send_error.slice(0, 60)}{i.send_error.length > 60 ? "…" : ""}</span></span>
                                        )}
                                    </div>
                                </div>
                                {i.status === "send_failed" && (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => retrySend(i.id, e)}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") retrySend(i.id, e); }}
                                        data-testid={`crm-retry-${i.id}`}
                                        className={`shrink-0 inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300 text-xs font-semibold px-2.5 py-1 hover:bg-rose-500/20 cursor-pointer ${retryingId === i.id ? "opacity-60 pointer-events-none" : ""}`}
                                    >
                                        {retryingId === i.id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Retry
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

            {detail && <CRMDetailDrawer invitationId={detail.id} statuses={statuses} onClose={() => setDetail(null)} onChange={load} />}
        </div>
    );
}

function CRMDetailDrawer({ invitationId, statuses, onClose, onChange }) {
    const [inv, setInv] = useState(null);
    const [status, setStatus] = useState("");
    const [tags, setTags] = useState("");
    const [note, setNote] = useState("");
    const [savingStatus, setSavingStatus] = useState(false);
    const [addingNote, setAddingNote] = useState(false);
    const [followupStage, setFollowupStage] = useState("3d");
    const [draft, setDraft] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/recruit/crm/candidates/${invitationId}`);
            setInv(data);
            setStatus(data.status);
            setTags((data.tags || []).join(", "));
        } catch (err) { toast.error(formatApiError(err)); }
    }, [invitationId]);
    useEffect(() => { load(); }, [load]);

    const saveStatus = async () => {
        setSavingStatus(true);
        try {
            const nextTags = tags.split(",").map((t) => t.trim()).filter(Boolean);
            await api.patch(`/admin/recruit/crm/candidates/${invitationId}`, { status, tags: nextTags });
            toast.success("Updated.");
            load(); onChange();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSavingStatus(false); }
    };
    const addNote = async () => {
        if (!note.trim()) return;
        setAddingNote(true);
        try {
            await api.post(`/admin/recruit/crm/candidates/${invitationId}/notes`, { body: note, is_private: true });
            setNote(""); load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setAddingNote(false); }
    };
    const generateDraft = async () => {
        setAiLoading(true);
        try {
            const { data } = await api.post(`/admin/recruit/crm/candidates/${invitationId}/generate-followup`, { stage: followupStage }, { timeout: 90_000 });
            setDraft(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setAiLoading(false); }
    };
    const sendDraft = async () => {
        if (!draft) return;
        setSending(true);
        try {
            await api.post(`/admin/recruit/crm/candidates/${invitationId}/send-followup`, { stage: draft.stage, subject: draft.subject, body: draft.body });
            toast.success(`${draft.stage} follow-up sent.`);
            setDraft(null); load(); onChange();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    const [retrying, setRetrying] = useState(false);
    const retryFailed = async () => {
        setRetrying(true);
        try {
            await api.post(`/admin/recruit/crm/candidates/${invitationId}/retry-send`);
            toast.success("Retry queued — the row will update in a few seconds.");
            load(); onChange();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setRetrying(false); }
    };

    if (!inv) return null;
    const followupsSent = new Set(inv.followups_sent || []);

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="crm-drawer">
            <div className="w-full max-w-2xl bg-background h-full overflow-y-auto shadow-2xl border-l border-border" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-border sticky top-0 bg-background z-10">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <h2 className="font-heading font-medium text-2xl leading-tight truncate">{inv.candidate_name || inv.candidate_email}</h2>
                            <p className="text-sm text-muted-foreground truncate">{inv.candidate_email}</p>
                        </div>
                        <button onClick={onClose} className="text-muted-foreground p-1"><X size={20} /></button>
                    </div>
                </div>

                <div className="p-5 space-y-5">
                    {inv.status === "send_failed" && (
                        <div
                            data-testid="crm-drawer-send-failed-banner"
                            className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 flex items-start gap-3"
                        >
                            <AlertTriangle size={16} className="text-rose-600 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-rose-700 dark:text-rose-300">
                                    Email delivery failed
                                </div>
                                <div className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5 break-words">
                                    {inv.send_error || "Resend returned no id — check RESEND_API_KEY or Resend dashboard."}
                                </div>
                                {inv.send_failed_at && (
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        failed at {new Date(inv.send_failed_at).toLocaleString()}
                                        {inv.retry_count > 0 && <> · retried {inv.retry_count}×</>}
                                    </div>
                                )}
                            </div>
                            <Button
                                size="sm"
                                onClick={retryFailed}
                                disabled={retrying}
                                data-testid="crm-drawer-retry-send"
                                className="bg-rose-600 hover:bg-rose-700 text-white shrink-0"
                            >
                                {retrying ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                                Retry send
                            </Button>
                        </div>
                    )}
                    <div>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">Status</div>
                        <div className="flex flex-wrap gap-2">
                            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="crm-status-select" className="text-sm bg-background border border-border rounded-md px-2 py-1.5">
                                {statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL(s)}</option>)}
                            </select>
                            <Input placeholder="tags, comma, separated" value={tags} onChange={(e) => setTags(e.target.value)} className="max-w-xs" data-testid="crm-tags-input" />
                            <Button onClick={saveStatus} disabled={savingStatus} data-testid="crm-save-status">{savingStatus ? <Loader2 size={13} className="animate-spin" /> : "Save"}</Button>
                        </div>
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">AI Follow-up Copilot</div>
                        <div className="rounded-lg border border-border bg-muted/20 p-3">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="inline-flex rounded-md border border-border overflow-hidden">
                                    {["3d", "7d", "14d"].map((s) => (
                                        <button key={s} onClick={() => setFollowupStage(s)} data-testid={`followup-stage-${s}`}
                                            className={`px-3 py-1 text-xs font-medium ${followupStage === s ? "bg-foreground text-background" : "bg-background"} ${followupsSent.has(s) ? "opacity-50" : ""}`}
                                            disabled={followupsSent.has(s)}>
                                            {s}{followupsSent.has(s) ? " ✓" : ""}
                                        </button>
                                    ))}
                                </div>
                                <Button size="sm" onClick={generateDraft} disabled={aiLoading || followupsSent.has(followupStage)} data-testid="followup-generate">
                                    {aiLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}
                                    Generate draft
                                </Button>
                            </div>
                            {draft && (
                                <div className="mt-3 space-y-2" data-testid="followup-draft">
                                    <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="followup-subject" />
                                    <Textarea rows={8} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="followup-body" />
                                    <div className="flex justify-end gap-2">
                                        <Button variant="ghost" onClick={() => setDraft(null)}>Discard</Button>
                                        <Button onClick={sendDraft} disabled={sending} data-testid="followup-send">
                                            {sending ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
                                            Send follow-up
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5 flex items-center gap-1"><StickyNote size={11} /> Notes</div>
                        <div className="flex gap-2">
                            <Input placeholder="Add a private note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="crm-note-input" onKeyDown={(e) => e.key === "Enter" && addNote()} />
                            <Button onClick={addNote} disabled={addingNote || !note.trim()} data-testid="crm-note-add">Add</Button>
                        </div>
                        <div className="mt-3 space-y-2">
                            {(inv.notes || []).map((n) => (
                                <div key={n.id} className="text-sm border-l-2 border-border pl-3">
                                    <div className="whitespace-pre-wrap">{n.body}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">by {n.author_email} · {new Date(n.created_at).toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
